import { useUpdateCustomEventSettings } from 'api/CustomEventsAPI';
import {
  useAllowEvents,
  useCreateCustomEventQuery,
  useDisallowEvents,
  useNotSaveEvents,
  useSaveEvents,
} from 'api/MappingsAPI';
import { getFeatureFlags } from 'core/userContext';
import { useSearchParamsContext } from 'modules/dataDictionary/contexts';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import { ErrorActionType } from 'modules/toastContainer/types';
import { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApiError } from 'services/api';
import { sentenceCase } from 'utils/stringHelpers';
import { isString } from 'utils/tsHelpers';
import { updateDeletionPoliciesRequest } from '../actions';
import { DEACTIVATED_PERMISSION, DISCARDED_PERMISSION, SAVED_PERMISSION } from '../constants';
import { useCustomEventsQueryContext, useFiltersContext, useModalsContext } from '../contexts';
import { INGESTION_POLICIES, MappingErrorResponse } from '../types';
import { createAddEventPayload, createBlockAllowPayload, createPermissionsPayload } from '../utils';

export const useUpdateIngestionPolicies = () => {
  const dispatch = useDispatch();
  const { SchemaManagementUI } = useSelector(getFeatureFlags);
  // This hook must be used within context of the CustomEventsQueryContextProvider
  const { project, refetchCustomEventMappings, refetchProject } = useCustomEventsQueryContext();
  const { setIngestionPolicyFilter: setIngestionPolicyFilters } = useFiltersContext(); // using wrong context here
  const { setIngestionPolicy } = useSearchParamsContext();
  const { hideModal } = useModalsContext();
  const setIngestionPolicyFilter = SchemaManagementUI
    ? setIngestionPolicy
    : setIngestionPolicyFilters;

  /** Updates event state in customEventSettings */
  const { mutate: mutateCustomEventSettings } = useUpdateCustomEventSettings(project?.id, {
    onError: () => {
      dispatch(addNegativeToast());
      hideModal();
    },
  });

  /** Creates an event in Mappings tracker */
  const { mutate: mutateCustomEventMapping } = useCreateCustomEventQuery();

  const handleMappingsError = (error: unknown, actionName: string) => {
    dispatch(
      addNegativeToastWithErrorId(
        'Error updating custom event.',
        `useUpdateIngestionPolicies/${actionName}`,
        error,
      ),
    );
    hideModal();
  };

  /** Change event `saved` state in Mappings tracker */
  const { mutate: saveEvents } = useSaveEvents({
    onError: (error) => {
      handleMappingsError(error, 'useSaveEvents');
    },
  });

  /** Change event `saved` state in Mappings tracker */
  const { mutate: notSaveEvents } = useNotSaveEvents({
    onError: (error) => {
      handleMappingsError(error, 'useNotSaveEvents');
    },
  });

  /** Change event `allowed` state in Mappings tracker */
  const { mutate: allowEvents } = useAllowEvents({
    onError: (error) => {
      handleMappingsError(error, 'useAllowEvents');
    },
  });

  /** Change event `allowed` state in Mappings tracker */
  const { mutate: disallowEvents } = useDisallowEvents({
    onError: (error) => {
      handleMappingsError(error, 'useDisallowEvents');
    },
  });

  const handleSuccess = (message: ReactNode, actions?: ErrorActionType[]) => {
    refetchProject();
    refetchCustomEventMappings();
    dispatch(addPositiveToast(message, { actions }));
    hideModal();
  };

  /** Controls block/allow */
  const updateGlobalIngestionPolicy = (allowed: boolean, successMsg: string) => () => {
    const payload = createBlockAllowPayload(allowed, project);
    mutateCustomEventSettings(payload, { onSuccess: () => handleSuccess(successMsg) });
  };
  const block = updateGlobalIngestionPolicy(false, 'Successfully blocked events.');
  const allow = updateGlobalIngestionPolicy(true, 'Successfully allowed events.');

  /** Creates a new custom event */
  const add = (customEventName: string, retentionPeriod: number, projectId: number) => {
    /** 1. Add event to mapping tracker */
    mutateCustomEventMapping(
      { projectId, customEventName },
      {
        onError: (error) => {
          const { response } = error as ApiError;
          const { fieldName = 'custom event', reason } = response?.data as MappingErrorResponse;
          const responseMessage = isString(reason) ? sentenceCase(reason) : '';
          const errorMessage = `Error adding ${fieldName}. ${responseMessage}`;
          dispatch(
            addNegativeToastWithErrorId(errorMessage, 'useUpdateIngestionPolicies/add', error),
          );
        },
        onSuccess: () => {
          /** 2. Update custom event settings (if mappings addition is successful) */
          const payload = createAddEventPayload(customEventName, project);
          mutateCustomEventSettings(payload);

          /** 3. Update deletion policies */
          dispatch(
            updateDeletionPoliciesRequest({
              events: [customEventName],
              isInitial: true,
              projectId,
              retentionPeriod,
            }),
          );

          /** 4. Refetch custom events and display toast message */
          handleSuccess('Successfully added event.');
        },
      },
    );
  };

  /** Changes event state to Saved: { allowed: true, saved: true } */
  const save = (eventName: string) => {
    const payload = createPermissionsPayload(eventName, SAVED_PERMISSION, project);
    mutateCustomEventSettings(payload, {
      onSuccess: () => {
        handleSuccess(`${eventName} successfully saved`, [
          {
            title: 'View saved events',
            onClick: () => setIngestionPolicyFilter(INGESTION_POLICIES.SAVED),
          },
        ]);
      },
    });
    saveEvents({ events: [eventName], projectId: project.id });
    allowEvents({ events: [eventName], projectId: project.id });
  };

  /** Changes event state to Discarded: { allowed: true, saved: false } */
  const discard = (eventName: string) => {
    const payload = createPermissionsPayload(eventName, DISCARDED_PERMISSION, project);
    mutateCustomEventSettings(payload, {
      onSuccess: () => {
        handleSuccess(`${eventName} successfully discarded`, [
          {
            title: 'View discarded events',
            onClick: () => setIngestionPolicyFilter(INGESTION_POLICIES.DISCARDED),
          },
        ]);
      },
    });
    notSaveEvents({ events: [eventName], projectId: project.id });
    allowEvents({ events: [eventName], projectId: project.id });
  };

  /** Changes event state to Deactivated: { allowed: false, saved: false } */
  const deactivate = (eventName: string) => {
    const payload = createPermissionsPayload(eventName, DEACTIVATED_PERMISSION, project);
    mutateCustomEventSettings(payload, {
      onSuccess: () => {
        handleSuccess(`${eventName} successfully deactivated`, [
          {
            title: 'View deactivated events',
            onClick: () => setIngestionPolicyFilter(INGESTION_POLICIES.DEACTIVATED),
          },
        ]);
      },
    });
    notSaveEvents({ events: [eventName], projectId: project.id });
    disallowEvents({ events: [eventName], projectId: project.id });
  };

  return {
    add,
    block,
    allow,
    save,
    discard,
    deactivate,
  };
};
